<template>
  <div class="space-y-2">
    <!-- Zoom slider -->
    <!-- <div class="flex items-center gap-2">
      <span class="text-xs text-text-secondary">Zoom:</span>
      <input
        v-model="zoomLevel"
        type="range"
        :min="minZoom"
        max="200"
        class="w-full h-2 accent-primary bg-surface rounded-lg appearance-none cursor-pointer"
        @input="handleZoomChange"
      />
      <span class="text-xs text-text-secondary">{{ Math.round(zoomLevel) }}</span>
    </div> -->
    <!-- Progress bar at the top -->    
    <!-- Waveform container -->
    <div
      ref="waveformContainer"
      class="relative overflow-hidden bg-surface rounded-lg"
    >
      <!-- Loading indicator -->
      <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center bg-surface/50 z-1">
        <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-text-primary"></div>
      </div>

      <!-- Center alignment indicator (fixed playhead) -->
      <div class="absolute top-0 bottom-0 w-0.5 bg-text-primary left-1/2 transform -translate-x-1/2 pointer-events-none z-10"></div>

      <!-- Waveform display -->
      <div 
        ref="waveformDisplay" 
        class="waveform-display bg-surface cursor-move relative opacity-0 transition-opacity duration-100 z-0"
        :class="{ 'h-24': !isMobileDevice, 'h-12': isMobileDevice }"
        @mousedown.prevent="startOffsetDrag"
        @touchstart.prevent="handleTouchStart"
        @touchmove.prevent="handlePinch"
        @touchend.prevent="endPinch"
        @click="handleWaveformClick"
        @wheel.prevent="handleWheel"
      >
        <!-- Beat markers (for alignment with sheet music) -->
        <div class="beat-markers-container absolute pointer-events-none">
          <div 
            v-for="(marker, index) in beatMarkers" 
            :key="index" 
            class="absolute top-0 h-2 w-0.5 bg-text-primary pointer-events-none"
            :style="{ left: `${marker}px` }"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDeviceDetection } from '@/composables/useDeviceDetection';
import { useHorizontalDrag } from '@/composables/useHorizontalDrag';
import { useSettings } from '@/composables/useSettings';
import { useWaveSurfer } from '@/composables/useWaveSurfer';
import { hexToRgba } from '@/utils';
import { useResizeObserver } from '@vueuse/core';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, unref, watch, type MaybeRef } from 'vue';
import type WaveSurfer from 'wavesurfer.js';
import RegionsPlugin, { type Region } from 'wavesurfer.js/dist/plugins/regions.esm.js';

const props = defineProps<{
  audioFile: File
  timingMap?: Array<{ startMs: number; durationMs: number; beatsPerMeasure: number }>
  /**
   * Accepts a ref so a parent can hand over the clock without reading it. This value
   * changes every frame during play, and a parent that reads `.value` in its own
   * template re-renders that whole subtree 120 times a second to pass it down —
   * allocating a fresh vnode tree per frame for a number only this component uses.
   */
  currentPlayTime?: MaybeRef<number>
  isPlaying?: boolean
  loopSection?: { start: number; end: number } | null // in milliseconds
  loopEnabled?: boolean
  playbackRate?: number // 0.5–1.0; pitch is preserved
  /**
   * Whether the waveform is actually on screen. GameArea mounts this component purely
   * as an audio engine, inside a permanently hidden container — and `currentPlayTime`
   * updates every frame during play, so the scroll work in scrollWaveform would run a
   * forced layout read and two transform writes per frame for pixels nobody can see.
   */
  visible?: boolean
}>();

const audioOffset = defineModel<number>("audioOffset", { default: 0 });

// Unwrapped once, so the rest of the component reads a plain number regardless of
// whether the parent handed over a ref or a value.
const currentPlayTimeMs = computed(() => unref(props.currentPlayTime));

const emit = defineEmits<{
  (e: 'offset-drag-start'): void;
  (e: 'offset-drag-end'): void;
}>();

const { isMobileDevice } = useDeviceDetection();
const { audioVolume } = useSettings();
const { getWaveSurfer, getRegionsPlugin, loadAudioFile, setPlaybackRate, getProcessingLatencyMs, flushSoundTouchBuffer } = useWaveSurfer();

// State
const waveformDisplay = ref<HTMLElement>();
const waveformContainer = ref<HTMLElement>();
const wavesurfer = shallowRef<WaveSurfer>();
const regionsPlugin = shallowRef<RegionsPlugin>();
const activeLoopRegion = shallowRef<Region | null>(null);
const isLoading = ref(true);
const beatMarkers = ref<number[]>([]);
const durationInSecond = ref(0);
const zoomLevel = ref(1);
const fixedCursorX = ref(0);
const playbackTimeout = ref<number>();
const scheduledStartTime = ref(0);
const styles = getComputedStyle(document.documentElement);
// Touch gesture state
const isPinching = ref(false);
const initialPinchDistance = ref(0);
const initialZoomLevel = ref(0);

const minZoom = computed(()=>{
  if (!waveformContainer.value) return 0;
  return (waveformContainer.value.clientWidth ?? 0) / durationInSecond.value;
});
const accumulatedDelta = ref(0);
const startZoom = ref(0);
const deltaThreshold = ref(0);
const exponentialZooming = ref(false);
const oldX = ref(0);
const pointerTime = ref(0);
const maxZoom = ref(500);
const iterations = ref(20);
const scale = ref(0.5);

const handleWaveReady = async () => {
  if (!wavesurfer.value || !waveformDisplay.value) return;

  // Set up loop handling
  setupLoopHandling();

  // Get audio duration
  durationInSecond.value = wavesurfer.value.getDuration();
  const initialZoom = 100;
  waveformDisplay.value.style.width = `${initialZoom * durationInSecond.value}px`;
  zoomLevel.value = initialZoom;
  // Position waveform based on initial offset
  scrollWaveform(0);

  // Calculate beat markers using timing map
  createBeatMarkers();
  // Ensure loop region is rendered even when loop props arrived before audio ready.
  updateLoopRegion();

  setPlaybackRate(props.playbackRate ?? 1);

  setTimeout(()=>{
    isLoading.value = false;
    if (!waveformDisplay.value) return;
    waveformDisplay.value.style.opacity = "1";
  }, 200);
};

// Watch for audio volume changes
watch(() => audioVolume.value, (newVolume) => {
  if (wavesurfer.value) {
    wavesurfer.value.setVolume(Number(newVolume));
  }
}, { immediate: true });

watch(() => props.playbackRate, (rate) => {
  if (rate != null) setPlaybackRate(rate);
});

// Keep fixedCursorX aligned with the visual playhead (centered on waveformContainer) across resizes.
useResizeObserver(waveformContainer, () => {
  if (!waveformContainer.value) return;
  fixedCursorX.value = waveformContainer.value.clientWidth / 2;
  scrollWaveform(currentPlayTimeMs.value ?? 0);
});

// Initialize WaveSurfer
onMounted(async () => {
  if (!waveformDisplay.value || !waveformContainer.value) return;

  try {
    fixedCursorX.value = waveformContainer.value.clientWidth / 2;

    wavesurfer.value = getWaveSurfer({
      container: waveformDisplay.value,
      waveColor: styles.getPropertyValue("--color-text-tertiary"),
      progressColor: styles.getPropertyValue("--color-primary"),
      height: isMobileDevice.value ? 48 : 96,
    });
    regionsPlugin.value = getRegionsPlugin();
    
    // Set initial volume from settings
    wavesurfer.value.setVolume(Number(audioVolume.value));

    // Wait for the waveform to be ready
    wavesurfer.value.un('ready', handleWaveReady);
    wavesurfer.value.on('ready', handleWaveReady);
    
    // Load audio file
    await loadAudioFile(props.audioFile);
  } catch (error) {
    console.error('Error initializing waveform:', error);
    isLoading.value = false;
  }
});

// Watch for current time updates from parent
watch(currentPlayTimeMs, (currentPlayTime) => {
  if (currentPlayTime !== undefined && wavesurfer.value) {
    scrollWaveform(currentPlayTime);
  }
}, {immediate: true});

// Watch for play state changes
watch(() => props.isPlaying, (isPlaying) => {
  if (!wavesurfer.value) return;
  if (!isPlaying) {
    wavesurfer.value.pause();
    scheduledStartTime.value = 0;
    if (playbackTimeout.value) {
      clearTimeout(playbackTimeout.value);
      playbackTimeout.value = undefined;
    }
  }
});

// Watch for loop section changes
watch([() => props.loopSection, () => props.loopEnabled, () => wavesurfer.value], () => {
  updateLoopRegion();
});

watch(() => props.timingMap, () => {
  createBeatMarkers();
});

// Set up loop handling when regions plugin is ready
const setupLoopHandling = () => {
  if (!regionsPlugin.value) return;
  
  // Note: Loop handling is controlled by game engine, not by regions plugin
  // Regions are only used for visual indication of loop boundaries
  // The game engine will call seekToTime() when loop point is reached
};

// Watch for changes to offset and update UI
watch(audioOffset, () => {
  scrollWaveform(currentPlayTimeMs.value ?? 0);
});

const setWaveformDisplayWidth = async (newMinPxPerSec: number)=>{
  if (!waveformContainer.value || !waveformDisplay.value) return;
  const waveformContainerWidth = waveformContainer.value.clientWidth;
  if (newMinPxPerSec * durationInSecond.value < waveformContainerWidth) {
      waveformDisplay.value.style.width = `${waveformContainerWidth}px`;
    } else {
      // workaround to make it sync with mid target line
      waveformDisplay.value.style.width = `${newMinPxPerSec * durationInSecond.value - 1}px`;
      await nextTick();
      waveformDisplay.value.style.width = `${newMinPxPerSec * durationInSecond.value + 1}px`;
    }
}

const handleWheel = async (e: WheelEvent) => {
  if (!wavesurfer.value || ! waveformDisplay.value || !waveformContainer.value) return;
  // Update the accumulated delta...
  accumulatedDelta.value += -e.deltaY

  if (startZoom.value === 0 && exponentialZooming.value) {
    startZoom.value = waveformDisplay.value.clientWidth / wavesurfer.value.getDuration();
  }

  // ...and only scroll once we've hit our threshold
  if (deltaThreshold.value === 0 || Math.abs(accumulatedDelta.value) >= deltaThreshold.value) {
    const oldMinPxPerSec = waveformDisplay.value.clientWidth / durationInSecond.value;
    const x = e.clientX - waveformDisplay.value.getBoundingClientRect().left
    const scrollX = wavesurfer.value.getScroll();
    // Update pointerTime only if the pointer position has changed. This prevents the waveform from drifting during fixed zooming.
    if (x !== oldX.value || oldX.value === 0) {
      pointerTime.value = (scrollX + x) / oldMinPxPerSec
    }
    oldX.value = x

    const newMinPxPerSec = calculateNewZoom(oldMinPxPerSec, accumulatedDelta.value)
    await setWaveformDisplayWidth(newMinPxPerSec);
    wavesurfer.value.zoom(newMinPxPerSec);
    zoomLevel.value = Math.max(minZoom.value, newMinPxPerSec);
    wavesurfer.value.setScroll(0);
    scrollWaveform(currentPlayTimeMs.value ?? 0);
    // Reset the accumulated delta
    accumulatedDelta.value = 0
    await nextTick();
    createBeatMarkers();
  }
}


// TODO: Commented out as appears to be unused, but keeping for future reference
// const handleZoomChange = (e: Event) => {
//   const target = e.target as HTMLInputElement;
//   if (!target || !waveformContainer.value || !waveformDisplay.value) return;
//   
//   const minPxPerSec = parseInt(target.value);
//
//   if (wavesurfer.value) {
//     // Apply zoom (minPxPerSec is pixels per second)    
//     setWaveformDisplayWidth(minPxPerSec);
//     // console.log({minPxPerSec});
//     wavesurfer.value.zoom(minPxPerSec);
//     // Update waveform position after zoom
//     wavesurfer.value.setScroll(0);
//     scrollWaveform(currentPlayTimeMs.value ?? 0);
//     createBeatMarkers();
//   }
// };

const calculateNewZoom = (oldZoom: number, delta: number) => {
    let newZoom;
    if (exponentialZooming.value) {
      const zoomFactor =
        delta > 0
          ? Math.pow(maxZoom.value / startZoom.value, 1 / (iterations.value - 1))
          : Math.pow(startZoom.value / maxZoom.value, 1 / (iterations.value - 1))
      newZoom = Math.max(0, oldZoom * zoomFactor)
    } else {
      // Default linear zooming
      newZoom = Math.max(0, oldZoom + delta * scale.value)
    }
    return Math.min(newZoom, maxZoom.value);
  }

// Create visual markers for beats to help with alignment
const createBeatMarkers = () => {
  if (!waveformDisplay.value || !durationInSecond.value) return;
  const markers: number[] = [];
  const width = waveformDisplay.value.clientWidth;

  if (props.timingMap?.length) {
    const totalDurationMs = durationInSecond.value * 1000;
    const leadInMs = props.timingMap[0].durationMs;
    const firstBeats = props.timingMap[0].beatsPerMeasure;
    if (firstBeats > 0) {
      const preRollBeatMs = leadInMs / firstBeats;
      for (let t = 0; t < leadInMs; t += preRollBeatMs) {
        const position = t * width / totalDurationMs;
        if (position >= 0 && position <= width) markers.push(position);
      }
    }

    for (const segment of props.timingMap) {
      if (segment.beatsPerMeasure <= 0) continue;
      const beatDurationMs = segment.durationMs / segment.beatsPerMeasure;
      const segmentStartMs = leadInMs + segment.startMs;
      for (let i = 0; i < segment.beatsPerMeasure; i++) {
        const t = segmentStartMs + i * beatDurationMs;
        if (t > totalDurationMs) break;
        const position = t * width / totalDurationMs;
        if (position >= 0 && position <= width) markers.push(position);
      }
    }
  } else {
    for (let t = 0; t < durationInSecond.value * 1000; t += 500) {
      const position = t * width / (durationInSecond.value * 1000);
      if (position >= 0 && position <= width) {
        markers.push(position);
      }
    }
  }

  beatMarkers.value = markers;
};

// Update the waveform position based on the current time and offset
const scrollWaveform = (currentPlayTime: number) => {
  if (!wavesurfer.value || !waveformDisplay.value || !durationInSecond.value) return;

  // Audio position first: it is the only half that matters while hidden. Seeking a
  // paused waveform still has to land, so this runs whether or not anything is drawn.
  if (!props.isPlaying) {
    const targetAudioTime = Math.max(0, currentPlayTime - audioOffset.value) / 1000;
    wavesurfer.value.setTime(targetAudioTime);
  }

  // Everything below is presentation, and this runs on every frame of playback.
  if (!props.visible) return;

  const waveformContent = wavesurfer.value.getWrapper();
  const containerWidth = waveformContent.clientWidth;

  // Scroll needed to hold the playhead at fixedCursorX, plus the offset adjustment
  // converted from ms into a fraction of the total duration.
  const scrollPosition = (currentPlayTime / 1000 / durationInSecond.value) * containerWidth - fixedCursorX.value;
  const offsetPx = (audioOffset.value / (durationInSecond.value * 1000)) * containerWidth;

  waveformContent.style.transform = `translateX(${offsetPx - scrollPosition}px)`;

  // Beat markers ride along, so an offset change moves both together.
  const beatMarkersContainer = waveformDisplay.value.querySelector('.beat-markers-container');
  if (beatMarkersContainer instanceof HTMLElement) {
    beatMarkersContainer.style.transform = `translateX(${-scrollPosition}px)`;
  }
};

// Dragging the waveform nudges the audio offset. Sensitivity is zoom-relative:
// a given travel always covers the same fraction of the visible waveform.
let initialOffset = 0;
const { isDragging, start: startOffsetDrag, stop: stopOffsetDrag } = useHorizontalDrag({
  onStart: () => {
    if (!wavesurfer.value || !durationInSecond.value) return false;
    initialOffset = audioOffset.value;
    emit('offset-drag-start');
  },
  onMove: (deltaX) => {
    if (!wavesurfer.value || !durationInSecond.value) return;
    const containerWidth = wavesurfer.value.getWrapper().clientWidth;
    audioOffset.value =
      initialOffset + Math.round((deltaX / containerWidth) * durationInSecond.value * 1000);
  },
  onEnd: () => emit('offset-drag-end'),
});

// Handle touch start for both dragging and pinch zoom
const handleTouchStart = (e: TouchEvent) => {
  if (!waveformDisplay.value) return;
  
  // Check if this is a pinch gesture (two fingers)
  if (e.touches.length === 2) {
    isPinching.value = true;
    
    // Calculate the initial distance between the two touch points
    const touch1 = e.touches[0];
    const touch2 = e.touches[1];
    initialPinchDistance.value = Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY
    );
    
    initialZoomLevel.value = waveformDisplay.value.clientWidth / durationInSecond.value;
    
    // A second finger converts the gesture into a pinch — end the drag now so the
    // offset stops tracking and `offset-drag-end` fires exactly once.
    stopOffsetDrag();
  } else if (e.touches.length === 1) {
    startOffsetDrag(e);
  }
};

// Handle touch move for pinch zoom
const handlePinch = async (e: TouchEvent) => {
  if (!isPinching.value || !wavesurfer.value || !waveformDisplay.value || !waveformContainer.value) return;
  
  // Only process pinch gestures (two fingers)
  if (e.touches.length !== 2) return;
  
  // Calculate the current distance between the two touch points
  const touch1 = e.touches[0];
  const touch2 = e.touches[1];
  const currentDistance = Math.hypot(
    touch2.clientX - touch1.clientX,
    touch2.clientY - touch1.clientY
  );
  
  // Calculate the scale factor based on the change in distance
  const scaleFactor = currentDistance / initialPinchDistance.value;
  
  // Calculate the center point between the two touches
  const centerX = (touch1.clientX + touch2.clientX) / 2;
  const rect = waveformDisplay.value.getBoundingClientRect();
  const x = centerX - rect.left;
  
  // Get the current zoom level
  const oldMinPxPerSec = waveformDisplay.value.clientWidth / durationInSecond.value;
  const scrollX = wavesurfer.value.getScroll();
  
  // Update pointer time based on the center position
  pointerTime.value = (scrollX + x) / oldMinPxPerSec;
  oldX.value = x;
  
  // Calculate the new zoom level based on the initial zoom and scale factor
  const newMinPxPerSec = Math.max(
    minZoom.value,
    Math.min(maxZoom.value, initialZoomLevel.value * scaleFactor)
  );
  
  // Apply the new zoom level
  await setWaveformDisplayWidth(newMinPxPerSec);
  wavesurfer.value.zoom(newMinPxPerSec);
  zoomLevel.value = newMinPxPerSec;
  wavesurfer.value.setScroll(0);
  scrollWaveform(currentPlayTimeMs.value ?? 0);
  
  await nextTick();
  createBeatMarkers();
};

// Handle touch end for pinch zoom
const endPinch = () => {
  if (isPinching.value) {
    isPinching.value = false;
    initialPinchDistance.value = 0;
    initialZoomLevel.value = 0;
  }
};

// Click handling for direct positioning
const handleWaveformClick = (_e: MouseEvent) => {
  if (!waveformDisplay.value || isDragging.value || !wavesurfer.value || !durationInSecond.value) return;
  //TODO: anything we want to handle here?
};

// Start playback at a specific time (in ms)
const play = (startTimeMs: number = 0) => {
  if (!wavesurfer.value) return;
  // Discard SoundTouch's stale WSOLA output before the new source feeds in —
  // otherwise audio from the pre-pause position briefly emits after a seek.
  flushSoundTouchBuffer();
    // Debug / test / simulate low end device delayed start
  // setTimeout  (() => {
  //   wavesurfer.value?.play(startTimeMs/1000);
  // }, 1000);
 wavesurfer.value?.play(startTimeMs/1000);
};

// Update loop region based on props
const updateLoopRegion = () => {
  if (!regionsPlugin.value || !wavesurfer.value || !durationInSecond.value) return;
  
  // Clear existing loop region
  if (activeLoopRegion.value) {
    activeLoopRegion.value.remove();
    activeLoopRegion.value = null;
  }
  
  // Create new loop region if loopSection is provided
  if (props.loopSection && props.loopEnabled) {
    const durationSec = durationInSecond.value;
    const rawStartSec = props.loopSection.start / 1000;
    const rawEndSec = props.loopSection.end / 1000;
    const startSec = Math.max(0, Math.min(rawStartSec, durationSec));
    const endSec = Math.max(0, Math.min(rawEndSec, durationSec));
    if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) return;
    
    activeLoopRegion.value = regionsPlugin.value.addRegion({
      start: startSec,
      end: endSec,
      color: hexToRgba(styles.getPropertyValue('--color-primary').trim(), 0.2),
      drag: false,
      resize: false,
    });
  }
};

// Hold the track silent, then start it from its own beginning `delayMs` from now.
// Serves both ends of the offset: a count-in before the piece, and a mid-run
// reposition (loop wrap, scrub) that lands before the track starts — the latter
// arrives while the source is still playing the stretch we just left, so it is
// stopped and its buffered tail dropped rather than left running early.
const schedulePlay = (delayMs: number) => {
  scheduledStartTime.value = performance.now() + delayMs;
  wavesurfer.value?.pause();
  flushSoundTouchBuffer();
  wavesurfer.value?.setTime(0);
  if (playbackTimeout.value) {
    clearTimeout(playbackTimeout.value);
  }

  // Fire wavesurfer.play() `latencyMs` earlier in wall clock so SoundTouch's
  // WSOLA buffer is primed and the first audible sample emerges right when
  // the wait ends. scheduledStartTime stays at the audible moment so the
  // virtual pre-start clock keeps reporting until then.
  const latencyMs = getProcessingLatencyMs();
  const playFireMs = Math.max(0, delayMs - latencyMs);
  playbackTimeout.value = window.setTimeout(() => {
    if (wavesurfer.value) {
      play();
      scheduledStartTime.value = 0;
    }
  }, playFireMs);
};

// Get audio duration in ms
const getAudioDurationMilliseconds = (): number => {
  return durationInSecond.value * 1000;
};

// Get audio duration in seconds
const getAudioDurationInSecond = (): number => {
  return durationInSecond.value;
};

// The WSOLA buffer delay expressed in source-time ms rather than wall clock.
// The source advances at `playbackRate` per real ms, so a fixed wall-clock
// latency spans fewer source ms as tempo slows. Zero at rate 1 (worklet bypassed).
const getLatencyInSourceMs = (): number => getProcessingLatencyMs() * (props.playbackRate ?? 1);

// Get current playback time in seconds from WaveSurfer
const getCurrentTimeInSecond = (): number => {
  if (!wavesurfer.value) return 0;
  if (scheduledStartTime.value > 0) {
    // Counting up to the scheduled start, so the reported position stays on the
    // source's own scale: a real-time delta covers `playbackRate` source ms.
    // Without the scaling this virtual clock outruns the game clock at slow
    // tempo, and the engine's desync correction drags the game forward to match.
    return (performance.now() - scheduledStartTime.value) * (props.playbackRate ?? 1) / 1000;
  }
  const sourceTime = wavesurfer.value.getCurrentTime();
  if (!props.isPlaying) return sourceTime;
  // Report the audible position, not where the source has already advanced to.
  return sourceTime - getLatencyInSourceMs() / 1000;
};

// Get current playback time in milliseconds (helper)
const getCurrentTimeInMs = (): number => {
  return getCurrentTimeInSecond() * 1000;
};

// A backing track shorter than the piece runs out mid-run, leaving WaveSurfer
// paused at its own end while the game clock keeps going. Repositioning back
// inside the audio then has to restart it — `setTime` alone would leave it
// silently stopped, so a loop whose audio had already ended would never be heard
// again. Started one window early for the same reason the seeks compensate: the
// first audible sample then lands on `timeMs`.
const restartIfMediaEnded = (timeMs: number) => {
  if (!wavesurfer.value || !props.isPlaying) return false;
  // Mid count-in the source is deliberately idle until schedulePlay fires.
  if (scheduledStartTime.value > 0 || wavesurfer.value.isPlaying()) return false;
  // A target past the file's own end has no audio to restart — every loop pass
  // through a stretch the track doesn't cover would otherwise re-trigger play().
  if (timeMs >= getAudioDurationMilliseconds()) return false;
  play(timeMs + getLatencyInSourceMs());
  return true;
};

// Seek to specific time in milliseconds. For a discontinuous jump (user scrub,
// pre-roll, resume) — SoundTouch's FIFO still holds a window of samples from the
// pre-seek position, so drop them or they play out across the seam. The refilled
// buffer then takes one window to become audible, so start the source that far
// ahead: the first audible sample lands on `timeMs` and the game clock (with it,
// the metronome) needs no adjustment. Loop seams use `seekSourceForLoopSeam`.
const seekToTime = (timeMs: number) => {
  if (!wavesurfer.value) return;
  // Paused: nothing in flight, and getCurrentTimeInSecond reports the raw
  // source position — so seek straight to the target.
  if (!props.isPlaying) {
    wavesurfer.value.setTime(timeMs / 1000);
    return;
  }
  if (restartIfMediaEnded(timeMs)) return;
  flushSoundTouchBuffer();
  wavesurfer.value.setTime((timeMs + getLatencyInSourceMs()) / 1000);
};

// Reposition the source for a loop seam, deliberately leaving SoundTouch's
// pipeline untouched. The window still in flight is the loop's own tail, so
// letting it drain across the seam keeps the audio continuous while the loop
// start feeds in behind it — no flush means no refill gap. Callers must fire
// this one window (`getLatencyInSourceMs`) before their own clock wrap.
const seekSourceForLoopSeam = (timeMs: number) => {
  if (restartIfMediaEnded(timeMs)) return;
  wavesurfer.value?.setTime(timeMs / 1000);
};

// Clean up on component unmount
onBeforeUnmount(() => {
  if (wavesurfer.value) {
    wavesurfer.value.un('ready', handleWaveReady);
    wavesurfer.value.stop();
  }
  
  // Clear any pending timeouts
  if (playbackTimeout.value) {
    clearTimeout(playbackTimeout.value);
  }
});

// Expose methods for parent component
defineExpose({
  isLoading,
  play,
  schedulePlay,
  getAudioDurationInSecond,
  getAudioDurationMilliseconds,
  getCurrentTimeInSecond,
  getCurrentTimeInMs,
  getProcessingLatencyMs,
  getLatencyInSourceMs,
  seekToTime,
  seekSourceForLoopSeam,
});
</script> 
